import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import { brandText } from '../../../../config/branding';
import currentTimeZone from '../../../../config/timeZone';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  getBucketFromRange,
} from '../../../../shared-functions/ChartFunctions';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import dashboard from '../../../../utils/api/dashboard';

interface PenaltyRevenueChartProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * PenaltyRevenueChart component
 * Visualizes penalty revenue over time using a smooth area chart
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @param {any} param0.filters - The filters prop
 * @returns {any} The penalty revenue chart component.
 */
function PenaltyRevenueChart({ startDate, endDate, filters }: PenaltyRevenueChartProperties) {
  const { t } = useTranslation('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  const activeRangeReference = React.useRef<string>('');

  /**
   * Fetches penalty revenue data from the API and updates chart state.
   * @param {boolean} hardRefresh - Whether to perform a hard refresh.
   */
  const fetchPenaltyRevenue = async (hardRefresh = false) => {
    const rangeKey = `${startDate}_${endDate}`;
    activeRangeReference.current = rangeKey;
    try {
      setIsLoading(true);
      const { facilityIds, stationIds, deviceIds } = filters || {};
      const query: any = {
        startDate,
        endDate,
        timeZone: currentTimeZone,
        hardRefresh,
      };
      if (facilityIds && facilityIds?.length > 0) {
        query.accountCustomId = facilityIds;
      }
      if (stationIds && stationIds?.length > 0) {
        query.stationIds = stationIds;
      }
      if (deviceIds && deviceIds?.length > 0) {
        query.deviceIds = deviceIds;
      }
      const response = await dashboard.getPowerConsumedReport(query);

      // If user already switched to another range, ignore this response
      if (activeRangeReference.current !== rangeKey) return;

      const updatedTime = response?.data?.last_updated_time;
      let dataList: any[] = [];
      if (Array.isArray(response?.data?.result)) {
        dataList = response.data.result;
      } else if (Array.isArray(response?.data)) {
        dataList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        dataList = response.data.data;
      }

      if (response?.status === 200) {
        const { bucket, days } = getBucketFromRange(startDate, endDate);
        let processedData: any[] = [];

        const normalizedData = dataList.map((item: any) => {
          if (item.date && !item.month && !item.monthName) {
            const d = new Date(item.date);
            if (!Number.isNaN(d.getTime())) {
              const months = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
              ];
              return { ...item, month: months[d.getMonth()] };
            }
          }
          return item;
        });

        if (bucket === 'hour') {
          processedData = fillMissingLast24HrsGeneric(normalizedData);
        } else {
          processedData = fillMissingLastNDaysGeneric(normalizedData, days, 'en-US', startDate, endDate);
        }

        const labels = processedData.map((item: any) => item.label || item.date || '');
        const cpoRevenueData = processedData.map((item: any) =>
          Number(item.cpoRevenueShare ?? 0),
        );

        setChartData({
          labels,
          datasets: [
            {
              label: t('penaltyRevenue'),
              data: cpoRevenueData,
              fill: true,
              borderColor: '#16A34A',
              /**
               * Sets the background color using a linear gradient.
               * @param {any} context - The chart context.
               * @returns {any} The linear gradient object or null.
               */
              backgroundColor: (context: any) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;
                if (!chartArea) return null;
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, 'rgba(22, 163, 74, 0.4)');
                gradient.addColorStop(1, 'rgba(22, 163, 74, 0)');
                return gradient;
              },
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 4,
              borderWidth: 2,
            },
          ],
        });
      } else {
        setChartData({
          labels: [],
          datasets: [],
        });
      }

      if (updatedTime) {
        setLastUpdatedTime(updatedTime);
      }
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error('Error fetching penalty revenue data:', error);
      if (activeRangeReference.current === rangeKey) {
        setChartData({
          labels: [],
          datasets: [],
        });
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  /**
   * Effect to reload data when filters change.
   */
  useEffect(() => {
    fetchPenaltyRevenue(true);
  }, [filters, startDate, endDate]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
      },
      tooltip: {
        /**
         * Function to create tooltip bg color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            if (context.tooltipItems[0].dataset.label === t('penaltyRevenue')) {
              return 'rgba(22, 163, 74, 0.9)';
            }
          }
          return 'rgba(0, 0, 0, 0.8)';
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate title for chart tooltip
           * @param {any} context - The context
           * @returns {any} The result
           */
          title: (context: any) => {
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel;
          },
          /**
           * Function to show tooltip data
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            const datasetLabel = context?.dataset?.label;
            const rawValue = context?.raw;
            const formattedValue =
              typeof rawValue === 'number' ? ToFixedValue(rawValue, 2) : rawValue;
            return `${formattedValue} ${datasetLabel === t('penaltyRevenue') ? brandText.currency : ''}`;
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: true,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: true,
        },
      },
    },
  };

  return (
    <div className="penalty-revenue-chart-wrap">
      <div className="penalty-revenue-chart-header">
        <div className="title-wrapper">
          <h6>{t('penaltyRevenue')}</h6>
          <TooltipIcon text={t('penaltyRevenueChartTooltip')} infoColor="info" />
        </div>
        {/* <NavigateArrow className="header-arrow" onClick={handleNavigate} /> */}
      </div>
      <div className="penalty-revenue-chart-container">
        {isLoading ? (
          <div className="battery-loader-container">
            <BatteryLoader />
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
      <div className="refresh">
        {isRefreshing ? (
          <div className="loading">
            <LoaderIcon style={{ width: '20px', height: '20px' }} />
          </div>
        ) : (
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={() => {
              setIsRefreshing(true);
              fetchPenaltyRevenue(true);
            }}
            svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
          />
        )}
        <p className="updated-time">
          {t('lastUpdated')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}
        </p>
      </div>
    </div>
  );
}

PenaltyRevenueChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default PenaltyRevenueChart;
